<script lang="ts">
    import { onMount } from 'svelte';

    import { api } from '../js/api.js';
    import { auth } from '../js/auth.js';
    import type { ApiPaging, ApiResponse } from '../js/apiTypes.js';
    import { getNestedProperty } from '../js/utils.js';
    import {
        emptyTableData,
        getSelectOptions,
        optionProp,
        type IndexParams,
        type ListRecord,
        type TableData,
    } from '../js/tableTypes.js';

    import ModelView from './ModelView.svelte';
    import ModalConfirm from './ModalConfirm.svelte';

    export let displayViewer = false;
    export let modelName = '';
    export let tableData: TableData = emptyTableData;
    export let extraParms = '';
    export let customSubmit: ((data: FormData) => void) | null = null;

    let list: ListRecord[] = [];
    let paging: ApiPaging = { current_page: 0, last_page: 0 };
    let currentId: string | null = '';
    let currentPage = 1;
    let sortColumn = '';
    let sortDirection: 'asc' | 'desc' = 'asc';
    let searchPhrase = '';
    let searchOptions = new Map<string, string>();
    let displayConfirm = false;

    const createRecord = () => {
        showModel(null);
    };

    const setSearchPhrase = (phrase: string) => {
        searchPhrase = phrase;
        refreshTable();
    };

    const showModel = (id: string | null) => {
        currentId = id;
        displayViewer = true;
    };

    const deleteConfirm = (id: string) => {
        currentId = id;
        displayConfirm = true;
    };

    const deleteRecord = async () => {
        displayConfirm = false;
        if (currentId) {
            await api.crudDelete(modelName, currentId);
        }
        refreshTable();
    };

    const getIndex = async (parameters: IndexParams = {}) => {
        const response = await api.crudIndex(modelName, parameters, extraParms) as ApiResponse<ListRecord[]>;
        list = response.data ?? [];
        paging = response.paging ?? { current_page: 0, last_page: 0 };
        if (list === undefined) {
            list = [];
            paging = { current_page: 0, last_page: 0 };
        }
    };

    const refreshTable = () => {
        const searchOptionsObject = Object.fromEntries(searchOptions);
        getIndex({
            page: currentPage,
            phrase: searchPhrase,
            column: sortColumn,
            order: sortDirection,
            ...searchOptionsObject,
        });
    };

    const setCurrentPage = (page: number) => {
        currentPage = page;
        refreshTable();
    };

    onMount(async () => {
        if (tableData.details.sortby) {
            sortColumn = tableData.details.sortby;
        } else if (tableData.fields.length > 0) {
            sortColumn = tableData.fields[0].key;
        }
        setCurrentPage(1);
    });

    const sortTable = (column: string, sortable = true) => {
        if (sortable) {
            if (sortColumn === column) {
                sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
            } else {
                sortColumn = column;
                sortDirection = 'asc';
            }
            refreshTable();
        }
    };

    const setSearchOption = (searchKey: string, value: string) => {
        if (value === '') {
            searchOptions.delete(searchKey);
        } else {
            searchOptions.set(searchKey, value);
        }
        refreshTable();
    };

    function handleSearchKeydown(e: KeyboardEvent & { currentTarget: HTMLInputElement }) {
        if (e.key === 'Enter') {
            e.preventDefault();
            setSearchPhrase(e.currentTarget.value);
            refreshTable();
        }
    }

    function handleSelectChange(
        e: Event & { currentTarget: HTMLSelectElement },
        searchKey: string,
    ) {
        setSearchOption(searchKey, e.currentTarget.value);
    }
</script>

{#if $auth.token}

    <h2 class="ui header">{tableData.details.titleList}</h2>
    <div class="ui icon input">
        <i class="search icon"></i>
        {#each tableData.fields as field (field.key)}
        {#if field.type === 'select' && field.select?.searchable}
        <select on:change={(e) => handleSelectChange(e, field.select?.search_key ?? field.key)}>
            <option selected value=''>{`Any ${field.label}`}</option>
            {#each getSelectOptions($api.lists, field.select.data_list) as option (optionProp(option, field.select.key))}
            <option value={optionProp(option, field.select.key)}>{optionProp(option, field.select.display)}</option>
            {/each}
        </select>
        {/if}
        {/each}
        <input type="text" placeholder="Search..."
            on:keydown={handleSearchKeydown}>
    </div>
    {#if tableData.details.create}
    <button class="ui button" on:click={createRecord}>{ tableData.details.createLabel ?? 'New Record' }</button>
    {/if}

    <table class="ui compact striped table unstackable">
        <thead>
            <tr>
                {#each tableData.fields as field}
                    <th on:click={() => sortTable(field.key, field.sortable)}>
                        {field.label}
                        {#if field.sortable && sortColumn === field.key}
                            {#if sortDirection === 'asc'}
                            <i class="chevron down icon"></i>
                            {:else}
                            <i class="chevron up icon"></i>
                            {/if}
                        {/if}
                    </th>
                {/each}
                {#if tableData.details.edit || tableData.details.delete}
                <th class="one wide center aligned">Actions</th>
                {/if}
            </tr>
        </thead>
        <tbody>
            {#each list as item (item.uuid)}
                <tr>
                    {#each tableData.fields as field}
                    <td>{getNestedProperty(item, field.key)}</td>
                    {/each}
                    {#if tableData.details.edit || tableData.details.delete}
                    <td class="one wide center aligned">
                        <div class="ui icon tiny buttons">
                            {#if tableData.details.edit}
                            <button type="button" on:click={() => { showModel(item.uuid) }} title="edit" class="ui button">
                                <i class="edit icon"></i>
                            </button>
                            {/if}
                            {#if tableData.details.delete}
                                <button type="button" on:click={() => { deleteConfirm(item.uuid)}} title="delete" class="ui button">
                                    <i class="trash alternate outline icon"></i>
                                </button>
                            {/if}
                        </div>
                    </td>
                    {/if}
                </tr>
            {/each}
        </tbody>
    </table>
    {#if list.length === 0}
    <div class="ui icon message"><i class="info circle icon"></i><div class="content"><p>No records found.</p></div></div>
    {/if}

    {#if (paging.last_page ?? 0) > 1}
        <div class="ui pagination menu">
            {#if (paging.current_page ?? 0) > 0}
                <button type="button" on:click={() => setCurrentPage(0)} class="item" aria-label="First page"><i class="angle left icon" aria-hidden="true"></i></button>
                <button type="button" on:click={() => setCurrentPage(paging.current_page ?? 0)} class="item" aria-label="Previous page"><i class="angle double left icon" aria-hidden="true"></i></button>
            {/if}
            {#each Array.from({length: paging.last_page ?? 0}).map((_, i) => i) as page (page)}
                {#if page >= (paging.current_page ?? 0) - 5 && page <= (paging.current_page ?? 0) + 5}
                    <button type="button" on:click={() => setCurrentPage(page + 1)} class={page === paging.current_page ? 'active item' : 'item'}>
                        {page + 1}
                    </button>
                {/if}
            {/each}
            {#if (paging.current_page ?? 0) < (paging.last_page ?? 0) - 1}
                <button type="button" on:click={() => setCurrentPage((paging.current_page ?? 0) + 2)} class="item" aria-label="Next page"><i class="angle right icon" aria-hidden="true"></i></button>
                <button type="button" on:click={() => setCurrentPage(paging.last_page ?? 0)} class="item" aria-label="Last page"><i class="angle double right icon" aria-hidden="true"></i></button>
            {/if}
        </div>
    {/if}


<ModelView
    on:saved={() => setCurrentPage(currentPage)}
    bind:modelName={modelName}
    bind:modelId={currentId}
    bind:displayModal={displayViewer}
    {customSubmit}
    {tableData}
/>

<ModalConfirm on:yes="{deleteRecord}" bind:showModal={displayConfirm}>
    <div slot="body">
        Are you sure you want to delete this record?
    </div>
</ModalConfirm>

{:else}
<div class="ui icon message"><i class="info circle icon"></i><div class="content"><p>You must be logged in to view this page.</p></div></div>
{/if}
